// src/pages/OrderSuccess.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Copy, User, ShoppingBag, Package, Clock, MapPin, 
  Phone, CheckCircle, ArrowLeft, ArrowRight, Home, 
  Settings, UserCheck, Tag 
} from 'lucide-react';
import { ordersAPI, productsAPI } from '../services/api';
import './css/OrderSuccess.css'; 

const OrderSuccess = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [order, setOrder] = useState(null);
  const [enrichedItems, setEnrichedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isAuthenticated = !!localStorage.getItem('token');

  // Enhanced option label formatter with pricing
  const formatOptionWithPrice = (group, value) => {
    const extraPrice = Number(value.extra_price) || 0;
    return {
      groupName: group.name,
      valueName: value.value,
      extraPrice: extraPrice
    };
  };

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id || !['auth', 'guest'].includes(type)) {
        setError('Lien de commande invalide');
        setLoading(false);
        return;
      }

      try {
        let orderData;

        if (type === 'guest') {
          const response = await ordersAPI.getOrderByTrackingCode(id);
          orderData = response.data;
        } else if (type === 'auth' && isAuthenticated) {
          const response = await ordersAPI.getMyOrders();
          orderData = response.data.find(o => o.id === parseInt(id));
          if (!orderData) throw new Error('Commande introuvable');
        } else {
          throw new Error('Acces non autorise');
        }

        // Enrich items with full option details
        const itemsWithOptions = orderData.items.filter(item => {
          try {
            const opts = typeof item.selected_options === 'string' 
              ? JSON.parse(item.selected_options) 
              : item.selected_options;
            return opts && Object.keys(opts).length > 0;
          } catch {
            return false;
          }
        });

        let enriched = [...orderData.items];

        if (itemsWithOptions.length > 0) {
          const uniqueProductIds = [...new Set(itemsWithOptions.map(item => item.productId))];
          const productResponses = await Promise.allSettled(
            uniqueProductIds.map(pid => productsAPI.getById(pid))
          );

          const productsMap = {};
          productResponses.forEach((resp, idx) => {
            if (resp.status === 'fulfilled') {
              productsMap[uniqueProductIds[idx]] = resp.value.data;
            }
          });

          enriched = orderData.items.map(item => {
            let parsedOptions = {};
            try {
              parsedOptions = typeof item.selected_options === 'string' 
                ? JSON.parse(item.selected_options) 
                : item.selected_options;
            } catch {
              parsedOptions = {};
            }

            const fullProduct = productsMap[item.productId];
            if (!fullProduct || Object.keys(parsedOptions).length === 0) {
              return {
                ...item,
                finalPrice: Number(item.price || 0),
                optionDetails: null
              };
            }

            const basePrice = Number(fullProduct.salePrice || fullProduct.regularPrice || 0);
            let optionExtra = 0;
            const optionDetails = [];

            fullProduct.options.forEach(group => {
              const selectedValueId = parsedOptions[group.id];
              if (selectedValueId) {
                const value = group.values.find(v => v.id === Number(selectedValueId));
                if (value) {
                  const extra = Number(value.extra_price) || 0;
                  optionExtra += extra;
                  optionDetails.push(formatOptionWithPrice(group, value));
                }
              }
            });

            const finalPrice = basePrice + optionExtra;

            return {
              ...item,
              finalPrice,
              optionDetails: optionDetails.length > 0 ? optionDetails : null,
              name: fullProduct.name || item.name,
              selected_options: parsedOptions
            };
          });
        } else {
          enriched = enriched.map(item => ({
            ...item,
            finalPrice: Number(item.price || 0),
            optionDetails: null
          }));
        }

        setOrder(orderData);
        setEnrichedItems(enriched);
      } catch (err) {
        setError('Impossible de charger les details de la commande');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [type, id, isAuthenticated]);

  if (loading) {
    return (
      <div className="os-loading-container">
        <div className="os-loading-spinner"></div>
        <p className="os-loading-text">Chargement des details de la commande...</p>
      </div>
    );
  }

  if (error || !order || enrichedItems.length === 0) {
    return (
      <div className="os-error-container">
        <div className="os-error-card">
          <Package size={48} className="os-error-icon" />
          <p className="os-error-text">{error || 'Confirmation de commande invalide.'}</p>
          <button onClick={() => navigate('/')} className="os-error-button">
            <Home size={18} />
            Aller a l'accueil
          </button>
        </div>
      </div>
    );
  }

  const subtotal = enrichedItems.reduce((sum, item) => sum + item.finalPrice * item.quantity, 0);
  const discount = Number(order.discount_amount || 0);
  const deliveryFee = Number(order.delivery_fee || 0);
  const total = Math.max(0, subtotal - discount + deliveryFee);

  const handleCopyTrackingCode = () => {
    navigator.clipboard.writeText(order.trackingCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="os-container">
      {/* Enhanced Header */}
      <div className="os-header">
        <div className="os-header-content">
          <button onClick={() => navigate('/')} className="os-back-button">
            <ArrowLeft size={20} />
          </button>
          <div className="os-header-main">
            <CheckCircle size={32} className="os-success-icon" />
            <div>
              <h1 className="os-title">Commande confirmee !</h1>
              <p className="os-subtitle">
                {type === 'auth'
                  ? 'Votre commande est enregistree dans votre profil.'
                  : 'Merci pour votre achat. Votre commande est en cours de traitement.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Auth User Saved Message */}
      {type === 'auth' && (
        <div className="os-auth-saved-banner">
          <UserCheck size={18} />
          <span>Cette commande est enregistree dans votre compte. Vous pouvez la consulter a tout moment.</span>
        </div>
      )}

      {/* Tracking Code Section */}
      {type === 'guest' && (
        <div className="os-tracking-section">
          <div className="os-card os-tracking-card">
            <div className="os-card-header">
              <h2 className="os-card-title">Details de la commande</h2>
            </div>
            <div className="os-tracking-info">
              <div className="os-info-row">
                <span className="os-info-label">Commande</span>
                <span className="os-info-value os-order-id">#{order.id}</span>
              </div>
              <div className="os-info-row">
                <span className="os-info-label">Code de suivi</span>
                <div className="os-tracking-code-wrapper">
                  <span className="os-tracking-code">{order.trackingCode}</span>
                  <button 
                    onClick={handleCopyTrackingCode} 
                    className={`os-copy-button ${copied ? 'copied' : ''}`}
                  >
                    <Copy size={16} />
                    {copied ? 'Copie !' : 'Copier'}
                  </button>
                </div>
              </div>
              <div className="os-info-row">
                <span className="os-info-label">Date</span>
                <span className="os-info-value">
                  {new Date(order.createdAt).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="os-info-row">
                <span className="os-info-label">Statut</span>
                <span className="os-status-pending">En attente</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="os-main-grid">
        {/* Left Column - Order Items */}
        <div className="os-left-column">
          <div className="os-card os-items-card">
            <div className="os-card-header">
              <div className="os-header-flex">
                <div className="os-header-left">
                  <ShoppingBag size={20} />
                  <h2 className="os-card-title">Articles de la commande</h2>
                </div>
                <span className="os-items-count">
                  {enrichedItems.length} {enrichedItems.length === 1 ? 'article' : 'articles'}
                </span>
              </div>
            </div>

            <div className="os-items-list">
              {enrichedItems.map((item) => {
                const itemTotal = item.finalPrice * item.quantity;

                return (
                  <div key={item.id} className="os-item">
                    <div className="os-item-image-wrapper">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="os-item-image"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="os-item-placeholder">
                          <Package size={24} />
                        </div>
                      )}
                    </div>

                    <div className="os-item-details">
                      <h3 className="os-item-name">{item.name}</h3>

                      {/* Enhanced Options Display with Tags */}
                      {item.optionDetails && item.optionDetails.length > 0 && (
                        <div className="os-item-options">
                          {item.optionDetails.map((option, index) => (
                            <div key={index} className="os-option-tag">
                              <Tag size={12} />
                              <span className="os-option-group">{option.groupName}:</span>
                              <span className="os-option-value">{option.valueName}</span>
                              {option.extraPrice > 0 && (
                                <span className="os-option-price">
                                  +DT {option.extraPrice.toFixed(2)}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="os-item-footer">
                        <div className="os-item-meta">
                          <span className="os-item-quantity">Qté : {item.quantity}</span>
                          <span className="os-item-price">
                            DT {item.finalPrice.toFixed(2)} l'unite
                          </span>
                        </div>
                        <div className="os-item-total">
                          DT {itemTotal.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column - Order Summary */}
        <div className="os-right-column">
          <div className="os-card os-summary-card">
            <div className="os-card-header">
            <h2 className="os-card-title">Resume de la commande</h2>
            </div>

            <div className="os-summary-section">
              <div className="os-summary-row">
                <span className="os-summary-label">Sous-total</span>
                <span className="os-summary-value">DT {subtotal.toFixed(2)}</span>
              </div>

              {discount > 0 && (
                <div className="os-summary-row os-discount-row">
                  <span className="os-summary-label">
                    Remise
                    {order.promo_code && (
                      <span className="os-promo-badge">{order.promo_code}</span>
                    )}
                  </span>
                  <span className="os-summary-value">-DT {discount.toFixed(2)}</span>
                </div>
              )}

              <div className="os-summary-row">
                <span className="os-summary-label">Livraison</span>
                <span className="os-summary-value os-delivery-value">
                  {deliveryFee === 0 ? 'Gratuite' : `DT ${deliveryFee.toFixed(2)}`}
                </span>
              </div>

              <div className="os-summary-divider"></div>

              <div className="os-summary-row os-summary-total">
                <span className="os-summary-label-total">Total</span>
                <span className="os-summary-amount">DT {total.toFixed(2)}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="os-actions">
              {type === 'auth' ? (
                <button
                  onClick={() => navigate('/profile')}
                  className="os-button os-button-primary"
                >
                  <User size={20} />
                  <span>Voir dans le profil</span>
                  <ArrowRight size={18} className="os-button-arrow" />
                </button>
              ) : (
                <button
                  onClick={() => navigate('/order-tracking')}
                  className="os-button os-button-primary"
                >
                  <Package size={20} />
                  <span>Suivre cette commande</span>
                  <ArrowRight size={18} className="os-button-arrow" />
                </button>
              )}
              <button
                onClick={() => navigate('/')}
                className="os-button os-button-secondary"
              >
                <Home size={20} />
                <span>Continuer les achats</span>
              </button>
            </div>

            {/* Info Box */}
            <div className="os-info-box">
              <Clock size={18} className="os-info-box-icon" />
              <div className="os-info-box-content">
                <p className="os-info-box-title">Et ensuite ?</p>
                <p className="os-info-box-text">
                  {type === 'auth'
                    ? 'Vous pouvez consulter le statut a tout moment depuis votre profil.'
                    : 'Utilisez votre code de suivi sur la page de suivi pour suivre votre envoi.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
